import { MakeLogicType, actions, afterMount, kea, key, path, props, reducers, selectors } from 'kea'

import { LogsSparklineBreakdownBy } from '~/queries/schema/schema-general'
import { FilterLogicalOperator } from '~/types'

import {
    columnsToCustomColumns,
    DEFAULT_LOGS_COLUMNS,
    LogsColumnConfig,
    migrateAttributeColumns,
    normalizeColumns,
} from 'products/logs/frontend/components/LogsViewer/config/columns'
import { LogsViewerConfig, LogsViewerFilters } from 'products/logs/frontend/components/LogsViewer/config/types'
import type { LogsGroupBySourceEnumApi } from 'products/logs/frontend/generated/api.schemas'
import { AttributeColumnConfig, LogsOrderBy } from 'products/logs/frontend/types'

import type { DateRange, LogSeverityLevel } from '../../../../../../frontend/src/queries/schema/schema-general'
import type { UniversalFiltersGroup } from '../../../../../../frontend/src/types'

export const DEFAULT_LOGS_VIEWER_CONFIG: LogsViewerConfig = {
    filters: {
        dateRange: { date_from: '-1h', date_to: null },
        searchTerm: '',
        severityLevels: [],
        serviceNames: [],
        filterGroup: { type: FilterLogicalOperator.And, values: [] },
    },
}

export const DEFAULT_SPARKLINE_BREAKDOWN_BY: LogsSparklineBreakdownBy = 'severity'

export const DEFAULT_ORDER_BY: LogsOrderBy = 'latest'

export type LogsViewerViewMode = 'logs' | 'patterns' | 'group'
export const DEFAULT_VIEW_MODE: LogsViewerViewMode = 'logs'

// Patterns compare-mode baseline: 'lastWeek' omits the explicit range so the backend defaults
// to the same window one week earlier (absorbs daily/weekly cycles); 'preceding' compares
// against the window immediately before the current one (the post-deploy / spike comparison).
export type PatternsBaselineMode = 'lastWeek' | 'preceding'

export interface LogsViewerGroupBy {
    key: string
    // Where the key lives, in the group-by endpoint's vocabulary: "log" / "resource"
    // attribute maps, or "column" for top-level log fields (severity_level, trace_id, span_id).
    source: LogsGroupBySourceEnumApi
}

// Mirrors the endpoint's MAX_GROUP_DIMENSIONS (group_by_query_runner.py) — the picker must
// not offer a combination the API would reject.
export const MAX_GROUP_BY_DIMENSIONS = 4

export interface LogsViewerConfigProps {
    id: string
    defaultFacetRailCollapsed?: boolean
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface logsViewerConfigLogicValues {
    baselineMode: PatternsBaselineMode
    columns: LogsColumnConfig[]
    compareEnabled: boolean
    config: LogsViewerConfig
    customColumns: string[] | undefined
    facetRailCollapsed: boolean
    filters: LogsViewerFilters
    groupBys: LogsViewerGroupBy[]
    orderBy: LogsOrderBy
    sparklineBreakdownBy: LogsSparklineBreakdownBy
    sparklineCollapsed: boolean
    viewMode: LogsViewerViewMode
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface logsViewerConfigLogicActions {
    addColumn: (column: LogsColumnConfig) => {
        column: LogsColumnConfig
    }
    addGroupBy: (groupBy: LogsViewerGroupBy) => {
        groupBy: LogsViewerGroupBy
    }
    moveColumn: (
        id: string,
        direction: 'left' | 'right'
    ) => {
        direction: 'left' | 'right'
        id: string
    }
    removeColumn: (id: string) => {
        id: string
    }
    removeGroupByAt: (index: number) => {
        index: number
    }
    replaceGroupByAt: (
        index: number,
        groupBy: LogsViewerGroupBy
    ) => {
        groupBy: LogsViewerGroupBy
        index: number
    }
    setBaselineMode: (mode: PatternsBaselineMode) => {
        mode: PatternsBaselineMode
    }
    setColumnWidth: (
        id: string,
        width: number
    ) => {
        id: string
        width: number
    }
    setColumns: (columns: LogsColumnConfig[]) => {
        columns: LogsColumnConfig[]
    }
    setCompareEnabled: (enabled: boolean) => {
        enabled: boolean
    }
    setFacetRailCollapsed: (facetRailCollapsed: boolean) => {
        facetRailCollapsed: boolean
    }
    setFilter: (
        filter: keyof LogsViewerFilters,
        value: LogsViewerFilters[keyof LogsViewerFilters]
    ) => {
        filter: keyof LogsViewerFilters
        value: string | DateRange | UniversalFiltersGroup | string[] | LogSeverityLevel[] | undefined
    }
    setFilters: (filters: LogsViewerFilters) => {
        filters: LogsViewerFilters
    }
    setGroupBys: (groupBys: LogsViewerGroupBy[]) => {
        groupBys: LogsViewerGroupBy[]
    }
    setOrderBy: (
        orderBy: LogsOrderBy,
        source?: 'header' | 'toolbar'
    ) => {
        orderBy: LogsOrderBy
        source: 'header' | 'toolbar'
    }
    setSparklineBreakdownBy: (sparklineBreakdownBy: LogsSparklineBreakdownBy) => {
        sparklineBreakdownBy: LogsSparklineBreakdownBy
    }
    setViewMode: (viewMode: LogsViewerViewMode) => {
        viewMode: LogsViewerViewMode
    }
    toggleSparklineCollapsed: () => {
        value: true
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface logsViewerConfigLogicMeta {
    key: string
    __keaTypeGenInternalSelectorTypes: {
        config: (filters: LogsViewerFilters) => LogsViewerConfig
        customColumns: (columns: LogsColumnConfig[]) => string[] | undefined
    }
}

export type logsViewerConfigLogicType = MakeLogicType<
    logsViewerConfigLogicValues,
    logsViewerConfigLogicActions,
    LogsViewerConfigProps,
    logsViewerConfigLogicMeta
>

export const logsViewerConfigLogic = kea<logsViewerConfigLogicType>([
    path(['products', 'logs', 'frontend', 'components', 'LogsViewer', 'config', 'logsViewerConfigLogic']),
    props({ id: 'default' } as LogsViewerConfigProps),
    key((props) => props.id),

    actions({
        setFilters: (filters: LogsViewerFilters) => ({ filters }),
        setFilter: (filter: keyof LogsViewerFilters, value: LogsViewerFilters[keyof LogsViewerFilters]) => ({
            filter,
            value,
        }),
        setSparklineBreakdownBy: (sparklineBreakdownBy: LogsSparklineBreakdownBy) => ({ sparklineBreakdownBy }),
        setOrderBy: (orderBy: LogsOrderBy, source: 'header' | 'toolbar' = 'toolbar') => ({ orderBy, source }),
        toggleSparklineCollapsed: true,
        setFacetRailCollapsed: (facetRailCollapsed: boolean) => ({ facetRailCollapsed }),
        setViewMode: (viewMode: LogsViewerViewMode) => ({ viewMode }),
        setGroupBys: (groupBys: LogsViewerGroupBy[]) => ({ groupBys }),
        addGroupBy: (groupBy: LogsViewerGroupBy) => ({ groupBy }),
        removeGroupByAt: (index: number) => ({ index }),
        replaceGroupByAt: (index: number, groupBy: LogsViewerGroupBy) => ({ index, groupBy }),
        setCompareEnabled: (enabled: boolean) => ({ enabled }),
        setBaselineMode: (mode: PatternsBaselineMode) => ({ mode }),

        // Typed columns (unified column model)
        setColumns: (columns: LogsColumnConfig[]) => ({ columns }),
        addColumn: (column: LogsColumnConfig) => ({ column }),
        removeColumn: (id: string) => ({ id }),
        setColumnWidth: (id: string, width: number) => ({ id, width }),
        moveColumn: (id: string, direction: 'left' | 'right') => ({ id, direction }),
    }),

    reducers(({ props }) => ({
        filters: [
            DEFAULT_LOGS_VIEWER_CONFIG.filters,
            {
                setFilters: (_, { filters }) => filters,
                setFilter: (state, { filter, value }) => ({
                    ...state,
                    [filter]: value,
                }),
            },
        ],
        sparklineBreakdownBy: [
            DEFAULT_SPARKLINE_BREAKDOWN_BY as LogsSparklineBreakdownBy,
            { persist: true },
            {
                setSparklineBreakdownBy: (_, { sparklineBreakdownBy }) => sparklineBreakdownBy,
            },
        ],
        sparklineCollapsed: [
            false,
            { persist: true },
            {
                toggleSparklineCollapsed: (state) => !state,
            },
        ],
        facetRailCollapsed: [
            props.defaultFacetRailCollapsed ?? false,
            { persist: true },
            {
                setFacetRailCollapsed: (_, { facetRailCollapsed }) => facetRailCollapsed,
            },
        ],
        orderBy: [
            DEFAULT_ORDER_BY as LogsOrderBy,
            {
                setOrderBy: (_, { orderBy }) => orderBy,
            },
        ],
        // Not persisted — the Viewer always opens in Logs mode; Patterns is an explicit switch.
        viewMode: [
            DEFAULT_VIEW_MODE as LogsViewerViewMode,
            {
                setViewMode: (_, { viewMode }) => viewMode,
            },
        ],
        // Compare state lives here (not in logsPatternsLogic) so the choice survives
        // Logs↔Patterns switches within a visit — the patterns logic unmounts when leaving
        // the lens. Not persisted, like viewMode and groupBys.
        compareEnabled: [
            false,
            {
                setCompareEnabled: (_, { enabled }) => enabled,
            },
        ],
        baselineMode: [
            'lastWeek' as PatternsBaselineMode,
            {
                setBaselineMode: (_, { mode }) => mode,
            },
        ],
        columns: [
            DEFAULT_LOGS_COLUMNS,
            { persist: true },
            {
                setColumns: (_, { columns }) => normalizeColumns(columns),
                addColumn: (state, { column }) => normalizeColumns([...state, column]),
                removeColumn: (state, { id }) => state.filter((column) => column.id !== id),
                setColumnWidth: (state, { id, width }) =>
                    state.map((column) => (column.id === id ? { ...column, width } : column)),
                moveColumn: (state, { id, direction }) => {
                    const index = state.findIndex((column) => column.id === id)
                    const targetIndex = direction === 'left' ? index - 1 : index + 1
                    if (index === -1 || targetIndex < 0 || targetIndex >= state.length) {
                        return state
                    }
                    const next = [...state]
                    ;[next[index], next[targetIndex]] = [next[targetIndex], next[index]]
                    return normalizeColumns(next)
                },
            },
        ],
        // The Group view's configuration: the ordered dimensions to group by. Kept separate
        // from viewMode so the choice survives switching
        // lenses within a visit — Logs and back returns to the same grouping. Empty = no key
        // chosen yet (empty state). Not persisted across visits — grouping is an explicit,
        // per-visit exploration like Patterns.
        groupBys: [
            [] as LogsViewerGroupBy[],
            {
                setGroupBys: (_, { groupBys }) => groupBys,
                // Cap and duplicate guards live here, not in the UI: the picker disables
                // in-use keys and hides the add affordance at the cap, but the reducer is
                // what guarantees the API never sees a combination it would reject.
                addGroupBy: (state, { groupBy }) =>
                    state.length >= MAX_GROUP_BY_DIMENSIONS ||
                    state.some((d) => d.key === groupBy.key && d.source === groupBy.source)
                        ? state
                        : [...state, groupBy],
                removeGroupByAt: (state, { index }) => state.filter((_, i) => i !== index),
                replaceGroupByAt: (state, { index, groupBy }) =>
                    state.some((d, i) => i !== index && d.key === groupBy.key && d.source === groupBy.source)
                        ? state
                        : state.map((d, i) => (i === index ? groupBy : d)),
            },
        ],
    })),

    selectors({
        config: [(s) => [s.filters], (filters: LogsViewerFilters): LogsViewerConfig => ({ filters })],

        // Lowered wire value for LogsQuery.customColumns — undefined until a custom column exists
        customColumns: [
            (s) => [s.columns],
            (columns: LogsColumnConfig[]): string[] | undefined => columnsToCustomColumns(columns),
        ],
    }),

    afterMount(({ actions, props, values }) => {
        // One-time migration of the legacy per-attribute column config, which lived (persisted)
        // on logsViewerLogic before the unified column model. Only runs while `columns` is
        // pristine, and removes the legacy key so a later reset-to-default cannot re-migrate.
        const legacyKey = `products.logs.frontend.components.LogsViewer.logsViewerLogic.${props.id}.attributeColumnsConfig`
        try {
            const raw = window.localStorage.getItem(legacyKey)
            if (raw && values.columns === DEFAULT_LOGS_COLUMNS) {
                const legacy: Record<string, AttributeColumnConfig> = JSON.parse(raw)
                const migrated = migrateAttributeColumns(legacy)
                if (migrated.length > 0) {
                    // Legacy table order was timestamp, attribute columns, message
                    const [timestamp, message] = DEFAULT_LOGS_COLUMNS
                    actions.setColumns([timestamp, ...migrated, message])
                }
                window.localStorage.removeItem(legacyKey)
            }
        } catch {
            // Unreadable legacy state: leave defaults in place
        }
    }),
])
